import { FeatureFlag, FilterReportType } from '@cbo/shared-library';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import DateRange from '@mui/icons-material/DateRange';
import EditIcon from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Draggable from 'react-draggable';
import { GridColDef, GridAggregationModel } from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactComponent as RedoSvg } from '../../../assets/redo.svg';
import { ReactComponent as UndoSvg } from '../../../assets/undo.svg';
import ActionsDialog from '../../components/ActionsDialog/ActionsDialog';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import Header from '../../components/Header/Header';
import UnsavedFormDataPrompt from '../../components/UnsavedDataPrompt/UnsavedFormDataPrompt';
import { useSidebar } from '../../components/SidebarNavigation/ncr-design-system-components/CustomSidebar/CustomSidebarContext';
import routes from '../../constants/routes';
import { useAccountPreferences } from '../../contexts/accountPreferencesContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useUsers } from '../../contexts/userContext';
import { paddingX3, pagePadding } from '../../lib/styles/commonStyles';
import { formatDate } from '../../utils';
import useUndoRedo from '../../utils/hooks/useUndoRedo';
import useAppBarToggle from '../../utils/hooks/useAppBarToggle';
import { yesterday } from '../../utils/reportingUtils/filterUtils';
import { menuItemSx } from '../../components/CentralDashboard/components/styles';
import CreateReportDialog, { DatasetColumn } from '../CentralReports/components/CreateReportDialog';
import handleEditReportNav, { checkTitleExists } from '../CentralReports/utils/EditReportUtils';
import { checkHasAllCustomReportsViewAccess } from '../CentralReports/utils/PublishReportUtils';
import invalidateCustomReportsQuery from '../CentralReports/utils/QueryUtils';
import {
  ColumnDefinition,
  ComparativeColumn,
  CustomMetric,
  CustomReportData,
  DatagridElement,
  GroupedColumn,
} from '../requests/index';
import {
  useCustomReportDeleteMutation,
  useCustomReportsSaveMutation,
  useCustomReportUpdateMutation,
  useExportCustomReportMutation,
} from '../requests/mutations';
import {
  useGetAllCustomMetricsQuery,
  useGetCustomReportsDatasetsQuery,
  useGetCustomReportsQuery,
  useGetReportDetailsQuery,
  useScheduledReportsByReportIdQuery,
} from '../requests/queries';
import queryKeys from '../requests/queryKeys';
import { ScheduleStatus } from '../../scheduleReports/types';
import EditFiltersPopover from './components/EditFiltersPopover';
import ReportFilters from './components/ReportFilters';
import { buildGridColDefs, NonDraggableSelectors } from './helpers';
import {
  breadcrumbStyles,
  customReportHeaderBarSx,
  customReportHeaderButtonGroupSx,
  customReportRootSx,
  customReportsMenuButtonSx,
  customReportViewHeaderGroupSx,
  draggablePopoverBackdropSx,
  editMenuContainerSx,
  editMenuHalfMarginBelow,
  editMenuSubTextSx,
  editMenuTitleSx,
  filtersFlexGrowSx,
  headerButtonsSx,
  lastEditedDateBoxSx,
  lastEditedDateIconSx,
} from './styles';
import { ReportFiltersFormValues } from './types';
import CustomReportsGridSection from './components/CustomReportsGridSection';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';

const tKey = 'dataGrid.export';

function CustomReports() {
  const { t } = useTranslation();
  const nodeRef = useRef(null);
  const { canUndo, canRedo, undo, redo, set, present } = useUndoRedo({});
  const { preferences } = useAccountPreferences();
  const { setCollapsed, isCollapsed } = useSidebar();
  const { hideAppBarToggle, showAppBarToggle } = useAppBarToggle();
  const { setSnackbarState } = useSnackbar();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const bypassNavigationRef = useRef(false);
  const user = useUsers();
  const cboCustomMetrics = useFeatureFlag(FeatureFlag.customMetrics);
  const isScheduleReportEnabled = useFeatureFlag(FeatureFlag.scheduledReport);
  const hasAllCustomReportsViewAccess = checkHasAllCustomReportsViewAccess(user);
  const initializedHistoryRef = useRef(false);
  const lastSnapshotRef = useRef<CustomReportData | null>(null);

  const [reportId, setReportId] = useState<number | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [returning, setReturning] = useState(false);
  const [reportData, setReportData] = useState<CustomReportData | null>(null);
  const [editFilterAnchorEl, setEditFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<FilterReportType[]>([]);
  const prevReportIdRef = useRef<number | null>(null);
  const [filterValues, setFilterValues] = useState<ReportFiltersFormValues | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [editMenuAnchorEl, setEditMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editTitleMenuAnchorEl, setEditTitleMenuAnchorEl] = useState<null | HTMLElement>(null);
  const previousSidebarStateRef = useRef<boolean | null>(null);

  const [editTitleValue, setEditTitleValue] = useState<string>('');
  const [editDescriptionValue, setEditDescriptionValue] = useState<string>('');

  const [activeSection, setActiveSection] = useState<number | '' | 'description' | 'filters'>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [addAbove, setAddAbove] = useState(false);
  const [addSectionAt, setAddSectionAt] = useState<number>(0);

  const anyMenuOpen = Boolean(editMenuAnchorEl) || Boolean(editTitleMenuAnchorEl) || Boolean(editFilterAnchorEl);

  const restoreSidebarOnExitEditMode = () => {
    if (previousSidebarStateRef.current !== null) {
      setCollapsed(previousSidebarStateRef.current);
      previousSidebarStateRef.current = null;
    }
    showAppBarToggle();
  };

  // Hide sidebar and burger button when entering edit mode, restore when exiting
  useEffect(() => {
    if (isEdit) {
      // Save current sidebar state before collapsing
      previousSidebarStateRef.current = isCollapsed;
      setCollapsed(true);
      hideAppBarToggle();
    } else {
      restoreSidebarOnExitEditMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, setCollapsed]);

  const isEditMode = isEdit || !reportId;
  const isViewMode = !isEdit && reportId;

  const { data: allDatasets } = useGetCustomReportsDatasetsQuery();
  const { data: customMetricsData } = useGetAllCustomMetricsQuery(0, 100, cboCustomMetrics);
  const saveCustomReport = useCustomReportsSaveMutation();

  const exportCustomReportMutation = useExportCustomReportMutation();
  const { data: allReports } = useGetCustomReportsQuery(true);
  const updateCustomReport = useCustomReportUpdateMutation();
  const deleteCustomReportMutation = useCustomReportDeleteMutation();

  const createFormData = useMemo<CustomReportData | null>(() => {
    const stateData = sessionStorage.getItem('customReportStateData');
    return stateData ? JSON.parse(stateData) : null;
  }, []);

  const { data: reportDetails, isLoading } = useGetReportDetailsQuery(reportId?.toString() ?? '');
  const isLoadingReportDetails = !!reportId && isLoading;

  const { data: scheduledReportsForReport } = useScheduledReportsByReportIdQuery(
    reportId,
    isScheduleReportEnabled && !!reportId
  );
  const isReportActivelyScheduled = (scheduledReportsForReport?.schedules ?? []).some(
    (s) =>
      s.reportName === String(reportId) &&
      (s.scheduleStatus === ScheduleStatus.SCHEDULED || s.scheduleStatus === ScheduleStatus.ACTIVE)
  );

  // Populate report data from either session storage (if creating new) or fetched data
  useEffect(() => {
    if (createFormData) {
      setReportData(createFormData);
      return;
    }

    if (reportDetails) {
      setReportData(reportDetails as CustomReportData);
    }
  }, [createFormData, reportDetails]);

  // Invalidate queries on unmount to avoid stale cache when navigating away
  useEffect(() => {
    const prevReportId = prevReportIdRef.current;

    // If reportId changed from one valid ID to another, invalidate the previous one
    if (!isNil(prevReportId) && !isNil(reportId) && prevReportId !== reportId) {
      queryClient.invalidateQueries(queryKeys.customReports.customReportById(prevReportId as number));
    }

    // Update ref to current reportId
    prevReportIdRef.current = reportId;
  }, [reportId, queryClient]);

  // Cleanup on unmount only
  useEffect(
    () => () => {
      if (!isNil(prevReportIdRef.current)) {
        queryClient.invalidateQueries(queryKeys.customReports.customReportById(prevReportIdRef.current as number));
      }
      setReportData(null);
    },
    [queryClient]
  );

  // Form reflects CustomReport shape and stays in sync with reportData
  const formContext = useForm<CustomReportData>({
    defaultValues: {
      title: '',
      description: '',
      category: 'Custom',
      detailsAndConfig: {
        data: {},
        elements: [],
        additionalFilters: [],
      },
    },
    mode: 'onChange',
  });

  const { reset, formState, watch } = formContext;
  const { title, description, detailsAndConfig } = formContext.getValues();
  const watchedFilters = watch('detailsAndConfig.additionalFilters');
  const watchedElements = watch('detailsAndConfig.elements');

  // Extract dataset name from report data for ease of use
  const datasetNames = useMemo(() => {
    const datasetNamesNonUnique = watchedElements?.flatMap(
      (elements) =>
        // Assume each datagrid element list only has one element, and that element only has one dataset
        // This will change once data visualization elements are introduced, but unsure if those will support multiple datasets
        Object.keys(elements[0].columns)[0]
    );
    return Array.from(new Set(datasetNamesNonUnique));
  }, [watchedElements]);

  const allColumns = useMemo(() => {
    if (!reportData || !datasetNames) return {};

    const customMetricsByDataset = (cboCustomMetrics ? customMetricsData?.pageContent ?? [] : []).reduce<
      Record<string, CustomMetric[]>
    >((acc, metric) => {
      const datasetKey = metric.dataset?.replace(/\s/g, '').toLowerCase();
      if (!datasetKey) return acc;
      if (!acc[datasetKey]) acc[datasetKey] = [];
      acc[datasetKey].push(metric);
      return acc;
    }, {});

    const datasets = datasetNames
      .map((name: string) => {
        const matchingDataset = allDatasets?.find((ds) => {
          // Match by dataset name after normalizing (removing spaces and case insensitive)
          const cleanedName = ds.datasetName.replace(/\s/g, '').toLowerCase();
          return cleanedName === name.toLowerCase();
        });

        const normalizedName = name.replace(/\s/g, '').toLowerCase();
        const customMetricColumns = (customMetricsByDataset[normalizedName] ?? []).map((metric, index) => ({
          id: -(index + 1),
          columnName: metric.name,
          columnType: metric.metricType ?? 'CustomMetric',
          isNumeric: ['number', 'currency', 'percentage'].includes((metric.metricType ?? '').toLowerCase()),
          filterType: undefined,
          customMetricId: metric.id,
          customMetricFormat: metric.format,
          isCustomMetric: true,
        }));

        return {
          [name]: [...(matchingDataset?.columns ?? []), ...customMetricColumns],
        } as Record<string, ColumnDefinition[]>;
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});

    return datasets;
  }, [reportData, allDatasets, datasetNames, customMetricsData, cboCustomMetrics]);

  const selectedColumns = useMemo(() => {
    if (!datasetNames || Object.keys(allColumns).length === 0) return {};

    // Go through all the elements in the report and merge columns for each dataset into a single array of column names
    const mergedColumns = watchedElements
      .flatMap((elements) => elements[0])
      .map((element) => element.columns)
      .reduce((acc, columns) => {
        if (!columns) return acc;
        const datasetName = Object.keys(columns)[0];
        acc[datasetName] = [...(acc[datasetName] || []), ...columns[datasetName]];
        return acc;
      }, {} as Record<string, string[]>);

    // Use the column names above to filter the full column definitions from allColumns, so we have access to other properties like filterType and isNumeric
    const filteredColumns: Record<string, ColumnDefinition[]> = {};
    datasetNames.forEach((name) => {
      const columnNames = mergedColumns[name] || [];
      const allColsForDataset = allColumns[name] || [];
      filteredColumns[name] = allColsForDataset.filter((col) => columnNames.includes(col.columnName));
    });

    return filteredColumns;
  }, [watchedElements, allColumns, datasetNames]);

  const filters: DatasetColumn[] = useMemo(() => {
    if (!selectedColumns || Object.keys(selectedColumns).length === 0) return [];

    const filterCols: Record<string, ColumnDefinition[]> = {};

    // Extract columns with filterType defined. These are the possible filters for the report.
    datasetNames.forEach((dataset) => {
      const cols = selectedColumns[dataset] || [];
      cols.forEach((col) => {
        if (!col.filterType) return;

        if (!filterCols[dataset]) {
          filterCols[dataset] = [];
        }

        filterCols[dataset].push(col);
      });
    });

    return (
      Object.keys(filterCols)?.flatMap((dataset) =>
        filterCols[dataset].map((col) => ({
          // Generate unique ID for filter based on dataset and column IDs
          id: `${dataset}_${col.id}`,
          optionId: col.id.toString(),
          datasetId: dataset,
          label: col.columnName,
          filterType: col.filterType,
        }))
      ) ?? []
    );
  }, [selectedColumns, datasetNames]);

  // When available filters change (e.g. due to removing sections/datasources), remove any selected filters that are no longer available
  useEffect(() => {
    setSelectedFilters((prev) => prev.filter((f) => filters.some((filter) => filter.filterType === f)));
  }, [filters]);

  // Keep RHF state aligned with the current reportData
  useEffect(() => {
    if (reportData) {
      // If we're creating from session (createFormData), keep initial defaults
      // so RHF computes dirty state against the original empty defaults.
      if (createFormData) {
        reset(reportData, { keepDefaultValues: true });
      } else {
        // Viewing existing report: preserve dirty state while editing
        reset(reportData, { keepDefaultValues: false, keepDirty: isEdit });
      }
      if (!initializedHistoryRef.current) {
        set(reportData);
        lastSnapshotRef.current = reportData;
        initializedHistoryRef.current = true;
      }
    }
  }, [reportData, reset, createFormData, isEdit, set]);

  // Initialize selectedFilters from loaded report data
  useEffect(() => {
    if (reportData?.detailsAndConfig?.additionalFilters) {
      setSelectedFilters(reportData.detailsAndConfig.additionalFilters);
    }
  }, [reportData, formContext]);

  // Extract report ID from URL if viewing existing report
  useEffect(() => {
    if (!createFormData) {
      const params = new URLSearchParams(location.search);
      const idParam = params.get('id');
      const editParam = params.get('edit');
      if (editParam === 'true') {
        setIsEdit(true);
      }
      if (idParam) {
        // Set reportId to trigger data fetch
        setReportId(Number(idParam));
        return;
      }
      // if neither session data nor valid id param, redirect to all reports and show error
      setSnackbarState({
        open: true,
        message: t('admin.customReports.noDataError'),
        color: 'warning',
      });
      history.push(routes.ALL_REPORTS);
    }
  }, [createFormData, history, location.search, setSnackbarState, t]);

  const allDatasetsWithMergedColumns = useMemo(() => {
    const customMetricsByDataset = (cboCustomMetrics ? customMetricsData?.pageContent ?? [] : []).reduce<
      Record<string, Set<string>>
    >((acc, metric) => {
      const datasetKey = metric.dataset?.replace(/\s/g, '').toLowerCase();
      if (!datasetKey) return acc;
      if (!acc[datasetKey]) acc[datasetKey] = new Set<string>();
      acc[datasetKey].add(metric.name);
      return acc;
    }, {});

    return datasetNames
      .map((name) => {
        const normalizedName = name.replace(/\s/g, '').toLowerCase();
        const customMetricNames = customMetricsByDataset[normalizedName] ?? new Set<string>();

        return {
          // Keep only regular columns in selectedColumns; custom metrics are tracked separately.
          // Keep customMetricIds always present when custom metrics are enabled,
          // even if the array is empty, to keep update payload shape consistent.
          [name]: {
            selectedColumns:
              selectedColumns[name]?.map((col) => col.columnName).filter((col) => !customMetricNames.has(col)) || [],
            ...(cboCustomMetrics ? { customMetricIds: detailsAndConfig?.data?.[name]?.customMetricIds ?? [] } : {}),
          },
        };
      })
      .reduce(
        (acc, curr) => ({ ...acc, ...curr }),
        {} as Record<string, { selectedColumns: string[]; customMetricIds?: string[] }>
      );
  }, [datasetNames, selectedColumns, detailsAndConfig?.data, customMetricsData, cboCustomMetrics]);

  // When present snapshot changes, reset RHF state to match
  useEffect(() => {
    if (!present || Object.keys(present).length === 0) return;
    reset(present);
    lastSnapshotRef.current = present as CustomReportData;
  }, [present, reset]);

  const columns: GridColDef[][] = useMemo(() => {
    if (!reportData) return [];
    return buildGridColDefs(detailsAndConfig, preferences, allColumns);
  }, [reportData, detailsAndConfig, preferences, allColumns]);

  // Enable MUI built-in aggregation by summing numeric columns
  const aggregationModel = useMemo<GridAggregationModel>(() => {
    const model: GridAggregationModel = {};

    if (!selectedColumns || datasetNames.length === 0) return model;

    datasetNames.forEach((name) => {
      selectedColumns[name]
        ?.filter((c) => c.isNumeric)
        .forEach((c) => {
          model[c.columnName] = 'sum';
        });
    });

    return model;
  }, [selectedColumns, datasetNames]);

  const navigateBack = (toHome = false) => {
    setIsEdit(false);
    history.push(
      reportId && !toHome
        ? `${routes.CUSTOM_REPORTS}?id=${encodeURIComponent(String(reportId))}&edit=false`
        : routes.ALL_REPORTS
    );
  };

  const getDetailsAndConfigWithMergedDataset = () => {
    // Restrict to custom metric columns actually selected in this report, so regular
    // dataset columns that happen to share a name with an unrelated tenant-level custom
    // metric are not incorrectly stripped from defaultSort.
    const customMetricColumnNames = new Set(
      cboCustomMetrics
        ? Object.values(selectedColumns)
            .flat()
            .filter((col) => col.isCustomMetric)
            .map((col) => col.columnName)
        : []
    );

    const sanitizedElements = detailsAndConfig.elements.map((elementGroup) =>
      elementGroup.map((element) => {
        if (!element.defaultSort?.length) return element;
        const validDefaultSort = element.defaultSort.filter((sort) => !customMetricColumnNames.has(sort.field));
        return {
          ...element,
          defaultSort: validDefaultSort.length > 0 ? validDefaultSort : undefined,
        };
      })
    );

    return {
      detailsAndConfig: {
        data: { ...allDatasetsWithMergedColumns },
        additionalFilters: selectedFilters,
        elements: sanitizedElements,
      },
    };
  };

  /* Format and save report data */
  const handleSave = () => {
    if (!selectedColumns || Object.keys(selectedColumns).length === 0) return;

    saveCustomReport.mutateAsync(
      { customReport: { ...formContext.getValues(), ...getDetailsAndConfigWithMergedDataset() } },
      {
        onSuccess: () => {
          sessionStorage.removeItem('customReportStateData');
          // Invalidate the customReports list to trigger refetch & reflect the new/updated report
          invalidateCustomReportsQuery(queryClient, hasAllCustomReportsViewAccess);
          // Invalidate custom metrics so usage counts reflect the newly saved report
          queryClient.invalidateQueries({ queryKey: ['allCustomMetrics'] });
          setSnackbarState({
            open: true,
            message: t('admin.customReports.successMessage', { name: reportData?.title }),
            color: 'success',
          });
          bypassNavigationRef.current = true;
          reset();
          navigateBack();
          bypassNavigationRef.current = false;
        },
        onError: () => {
          setSnackbarState({
            open: true,
            message: t('admin.customReports.errorMessage', { name: reportData?.title }),
            color: 'error',
          });
        },
      }
    );
  };

  // Helper: commit current form state to undo/redo
  const commitToHistory = () => {
    const current = formContext.getValues();
    if (!lastSnapshotRef.current || !isEqual(current, lastSnapshotRef.current)) {
      set(current);
      lastSnapshotRef.current = current;
    }
  };

  const handleOpenEditFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEditFilterAnchorEl(event.currentTarget);
  };

  const handleCloseEditFilters = (_event?: unknown, _reason?: 'backdropClick' | 'escapeKeyDown') => {
    setEditFilterAnchorEl(null);
    formContext.setValue('detailsAndConfig.additionalFilters', selectedFilters, { shouldDirty: true });
    commitToHistory();
  };

  const handleToggleFilter = (filterType: FilterReportType) => {
    setSelectedFilters((prev) =>
      prev.includes(filterType) ? prev.filter((id) => id !== filterType) : [...prev, filterType]
    );
  };

  const handleSaveEditedMetadata = () => {
    const formValues = formContext.getValues();
    updateCustomReport.mutateAsync(
      {
        reportId: reportId as number,
        customReport: {
          title: formValues.title,
          description: formValues.description,
          ...getDetailsAndConfigWithMergedDataset(),
        },
      },
      {
        onSuccess: () => {
          // Restore sidebar state before navigating away
          restoreSidebarOnExitEditMode();

          setSnackbarState({
            open: true,
            message: t('admin.customReports.editReports.successMessage', { name: reportData?.title }),
            color: 'success',
          });
          invalidateCustomReportsQuery(queryClient, hasAllCustomReportsViewAccess);
          queryClient.invalidateQueries({ queryKey: queryKeys.customReports.customReportById(reportId as number) });
          queryClient.invalidateQueries({ queryKey: queryKeys.ReportList.ReportDetails(reportId?.toString() ?? '') });
          // Invalidate custom metrics so usage counts reflect the updated report
          queryClient.invalidateQueries({ queryKey: ['allCustomMetrics'] });
          bypassNavigationRef.current = true;
          reset();
          navigateBack();
        },
        onError: () => {
          setSnackbarState({
            open: true,
            message: t('admin.customReports.editReports.errorMessage', { name: reportData?.title }),
            color: 'error',
          });
        },
      }
    );
  };

  // Set edit default values when opening the edit title menu
  const handleOpenEditTitleMenu = (e: React.MouseEvent<HTMLElement>) => {
    setEditTitleValue(title || '');
    setEditDescriptionValue(description || '');
    setEditTitleMenuAnchorEl(e.currentTarget);
  };

  const isDuplicateTitle = useMemo(
    () => checkTitleExists(editTitleValue, allReports ?? [], reportId),
    [editTitleValue, allReports, reportId]
  );

  // Close edit title menu and update form values to reflect edits
  const handleCloseEditTitleMenu = (_event?: unknown, _reason?: 'backdropClick' | 'escapeKeyDown') => {
    const isError = editTitleValue.trim().length === 0 || isDuplicateTitle;
    if (isError) {
      // Don't close menu & apply edits if validation errors exist
      return;
    }
    setEditTitleMenuAnchorEl(null);
    formContext.setValue('title', editTitleValue, { shouldDirty: true });
    formContext.setValue('description', editDescriptionValue, { shouldDirty: true });
    commitToHistory();
  };

  const handleUpdateSection = (
    sectionIndex: number,
    sectionDataset: string,
    newColumns: DatasetColumn[],
    groupedColumns: GroupedColumn[],
    comparativeColumns: ComparativeColumn[],
    sectionName: string,
    showSummaryRow: boolean,
    sortBy: string,
    sortOrder: 'asc' | 'desc'
  ) => {
    const columnNames = newColumns?.map((opt) => opt.label) ?? [];
    const selectedDataColumnNames = cboCustomMetrics
      ? newColumns.filter((opt) => !opt.optionId.startsWith('custommetric_')).map((opt) => opt.label)
      : columnNames;
    const customMetricIds = cboCustomMetrics
      ? newColumns
          .filter((opt) => opt.optionId.startsWith('custommetric_'))
          .map((opt) => opt.optionId.replace('custommetric_', ''))
      : [];

    // Custom metric columns are not valid defaultSort fields on the backend.
    // Only use sortBy if it refers to a regular (non-custom-metric) column.
    const customMetricColumnNames = cboCustomMetrics
      ? newColumns.filter((opt) => opt.optionId.startsWith('custommetric_')).map((opt) => opt.label)
      : [];
    const isSortByCustomMetric = customMetricColumnNames.includes(sortBy);
    const resolvedSortBy = isSortByCustomMetric ? '' : sortBy;

    const newData = datasetNames.map((name) => {
      const existingCustomMetricIds = detailsAndConfig?.data?.[name]?.customMetricIds ?? [];
      let resolvedCustomMetricIds: string[] = [];
      if (name === sectionDataset) {
        resolvedCustomMetricIds = customMetricIds;
      } else if (cboCustomMetrics) {
        resolvedCustomMetricIds = existingCustomMetricIds;
      }

      const datasetConfig = {
        selectedColumns:
          name === sectionDataset ? selectedDataColumnNames : detailsAndConfig?.data?.[name]?.selectedColumns ?? [],
        ...(cboCustomMetrics ? { customMetricIds: resolvedCustomMetricIds } : {}),
      };

      return {
        [name]: datasetConfig,
      };
    });

    // Remove any comparative columns that no longer have a corresponding column in the new set of columns
    const filteredComparativeColumns = comparativeColumns.filter((col) =>
      columnNames.some((colName) => colName === col.field)
    );

    const newElements = detailsAndConfig.elements
      .slice(0, sectionIndex)
      .concat([
        [
          {
            type: 'datagrid',
            name: sectionName,
            columns: { [sectionDataset]: columnNames },
            groupedColumns: { [sectionDataset]: groupedColumns },
            comparativeColumns: { [sectionDataset]: filteredComparativeColumns },
            showSummaryRow,
            defaultSort: resolvedSortBy ? [{ field: resolvedSortBy, sortDirection: sortOrder }] : undefined,
          },
        ],
      ])
      .concat(detailsAndConfig.elements.slice(sectionIndex + 1));
    const updatedDetailsAndConfig = {
      data: newData.reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      additionalFilters: detailsAndConfig?.additionalFilters ?? [],
      elements: newElements,
    };
    formContext.setValue('detailsAndConfig', updatedDetailsAndConfig, { shouldDirty: true });

    commitToHistory();
  };

  // Dismiss edits and close menu regardless of validation errors
  const handleDismissEditMenu = () => {
    setEditTitleMenuAnchorEl(null);
    setEditFilterAnchorEl(null);
    setEditTitleValue(title || '');
    setEditDescriptionValue(description || '');
    setSelectedFilters(detailsAndConfig?.additionalFilters ?? []);
  };

  const handleDeleteReport = async () => {
    if (!reportId) return;
    try {
      await deleteCustomReportMutation.mutateAsync({ id: reportId });
      queryClient.invalidateQueries({ queryKey: queryKeys.customReports.all });
      setShowDeleteModal(false);
      bypassNavigationRef.current = true;
      reset();
      navigateBack(true);
      setSnackbarState({
        open: true,
        message: t('admin.customReports.deleteSuccessSnackbar', { name: reportData?.title }),
        color: 'success',
      });
    } catch (e) {
      setSnackbarState({
        open: true,
        message: t('admin.customReports.deleteErrorSnackbar', { name: reportData?.title }),
        color: 'error',
      });
    }
  };

  const shouldBlockNavigation = useCallback(() => {
    if (!isEditMode) return false;
    const hasChanges = formState.isDirty || canUndo || canRedo;
    return hasChanges && !unsavedPromptOpen && bypassNavigationRef.current !== true;
  }, [formState.isDirty, canUndo, canRedo, unsavedPromptOpen, isEditMode]);

  const handleDeleteSection = (sectionIndex: number) => {
    const newElements = detailsAndConfig.elements
      .slice(0, sectionIndex)
      .concat(detailsAndConfig.elements.slice(sectionIndex + 1));

    const updatedDetailsAndConfig = {
      ...detailsAndConfig,
      elements: newElements,
    };
    formContext.setValue('detailsAndConfig', updatedDetailsAndConfig, { shouldDirty: true });
    commitToHistory();
  };

  const handleAddSection = (sectionDetails: DatagridElement | undefined, sectionIndex: number) => {
    const positionToInsert = addAbove ? sectionIndex : sectionIndex + 1;
    const elementToAdd = !sectionDetails
      ? detailsAndConfig.elements[sectionIndex][0]
      : { ...sectionDetails, name: t('admin.customReports.editReports.tableTitle', { number: positionToInsert + 1 }) };
    const newElements = detailsAndConfig.elements
      .slice(0, positionToInsert)
      .concat([[elementToAdd]])
      .concat(detailsAndConfig.elements.slice(positionToInsert));

    const updatedDetailsAndConfig = {
      ...detailsAndConfig,
      elements: newElements,
    };
    formContext.setValue('detailsAndConfig', updatedDetailsAndConfig, { shouldDirty: true });
    commitToHistory();
  };

  const handleMoveSection = (sectionIndex: number, direction: 'up' | 'down') => {
    const positionToInsert = direction === 'up' ? sectionIndex - 1 : sectionIndex;
    const newElements = detailsAndConfig.elements
      .slice(0, positionToInsert)
      .concat([...detailsAndConfig.elements.slice(positionToInsert, positionToInsert + 2).reverse()])
      .concat(detailsAndConfig.elements.slice(positionToInsert + 2));
    const updatedDetailsAndConfig = {
      ...detailsAndConfig,
      elements: newElements,
    };
    formContext.setValue('detailsAndConfig', updatedDetailsAndConfig, { shouldDirty: true });
    commitToHistory();
  };

  const exportCustomReportsGridData = async () => {
    if (!reportData || !filterValues || filterValues.sites.length === 0) return;

    const siteIds = filterValues.sites.map((site) => site.id ?? '');
    const requestPayload = {
      detailsAndConfig,
      queryRequest: {
        startDate: filterValues.dateRange.startDate ?? yesterday,
        endDate: filterValues.dateRange.endDate ?? yesterday,
        siteIds,
      },
    };
    exportCustomReportMutation.mutateAsync(requestPayload, {
      onSuccess: () =>
        setSnackbarState({
          open: true,
          message: t(`${tKey}.message`, {
            status: t(`${tKey}.status.success`),
            report: title,
            result: t(`${tKey}.result.success`),
          }),
          color: 'success',
        }),
      onError: () =>
        setSnackbarState({
          open: true,
          message: t(`${tKey}.message`, {
            status: t(`${tKey}.status.failure`),
            report: title,
            result: t(`${tKey}.result.failure`),
          }),
          color: 'error',
        }),
    });
  };

  const handleSetActiveSection = (newActiveSection: number | '' | 'description' | 'filters') => {
    // Don't change active section if any edit menu is open, to avoid UX issues with menus losing
    // their anchor or disappearing when hovering over a different section
    if (anyMenuOpen) return;

    setActiveSection(newActiveSection);
  };

  const sectionList = useMemo(
    () =>
      detailsAndConfig.elements.map(([element], index) => (
        <CustomReportsGridSection
          // eslint-disable-next-line react/no-array-index-key
          key={`${Object.keys(element.columns)[0]}-${index}-${isEditMode ? 'edit' : 'view'}`}
          canEdit={!anyMenuOpen}
          element={detailsAndConfig.elements[index][0]}
          selectedColumns={selectedColumns[Object.keys(element.columns)[0]]}
          filterValues={filterValues}
          isEditMode={isEditMode}
          sectionIndex={index}
          canMoveDown={index < detailsAndConfig.elements.length - 1}
          canDelete={detailsAndConfig.elements.length > 1}
          allColumns={allColumns}
          sectionName={element.name}
          showSummaryRow={element.showSummaryRow ?? true}
          columns={columns[index]}
          datasetName={Object.keys(element.columns)[0]}
          aggregationModel={aggregationModel}
          exportCustomReportsGridData={exportCustomReportsGridData}
          exportCustomReportMutation={exportCustomReportMutation}
          handleSetActiveSection={handleSetActiveSection}
          handleUpdateSection={handleUpdateSection}
          handleAddSection={handleAddSection}
          handleMoveSection={handleMoveSection}
          handleDeleteSection={handleDeleteSection}
          setAddAbove={setAddAbove}
          setAddSectionAt={setAddSectionAt}
          setShowCreateModal={setShowCreateModal}
        />
      )),
    // Limiting what causes the sections to rerender to boost performance and avoid sections briefly flickering their loading states
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      detailsAndConfig.elements,
      anyMenuOpen,
      selectedColumns,
      filterValues,
      isEditMode,
      allColumns,
      columns,
      aggregationModel,
    ]
  );

  return (
    <FormProvider {...formContext}>
      {/* --------------------- MENUS and DIALOGS --------------------- */}

      {/* Prompt to prevent navigating away with unsaved changes */}
      <UnsavedFormDataPrompt
        open={unsavedPromptOpen}
        when={!isEditMode && (formState.isDirty || canUndo || canRedo)}
        onCancel={() => setUnsavedPromptOpen(false)}
        onConfirm={() => {
          setUnsavedPromptOpen(false);
          reset();
          sessionStorage.removeItem('customReportStateData');
          restoreSidebarOnExitEditMode();
          if (returning) navigateBack();
        }}
        shouldBlockNavigation={shouldBlockNavigation}
        textTranslationKey='unsavedDataDialog'
      />

      {/* Dialog for confirming report deletion */}
      <ActionsDialog
        open={showDeleteModal}
        dataTestId='delete-confirm-dialog'
        onActionClick={handleDeleteReport}
        onClose={() => setShowDeleteModal(false)}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        dialogTitle={`${t('admin.customReports.deleteReport')}?`}
        dialogContentText={t('admin.customReports.deleteReportConfirmation')}
        dialogContentTextColor='text.secondary'
        dialogContentTextVariant='body1'
        actionButtonText={t('admin.customReports.delete')}
        actionButtonColor='error'
        disableActionButton={deleteCustomReportMutation.isLoading}
      />

      {/* Dialog for creating new data table */}
      <CreateReportDialog
        open={showCreateModal}
        addAdditionalMode
        handleClose={(newSection?: DatagridElement) => {
          setShowCreateModal(false);
          if (newSection) handleAddSection(newSection, addSectionAt);
        }}
      />

      {/* Edit menu for changing report edit state */}
      <Menu
        anchorEl={editMenuAnchorEl}
        open={Boolean(editMenuAnchorEl)}
        onClose={() => setEditMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        data-testid='custom-reports-menu'
      >
        <MenuItem
          onClick={() => {
            setEditMenuAnchorEl(null);
            handleEditReportNav(history, reportId);
          }}
          sx={menuItemSx}
        >
          {t('admin.customReports.editReport')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setShowDeleteModal(true);
          }}
          sx={menuItemSx}
        >
          {t('admin.customReports.deleteReport')}
        </MenuItem>
      </Menu>

      {/* Edit menu for changing title/description  */}
      <Draggable nodeRef={nodeRef} cancel={NonDraggableSelectors}>
        <Popover
          ref={nodeRef}
          slotProps={draggablePopoverBackdropSx}
          anchorEl={editTitleMenuAnchorEl}
          open={Boolean(editTitleMenuAnchorEl)}
          onClose={handleCloseEditTitleMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Grid container direction='column' sx={editMenuContainerSx}>
            <Grid container alignItems='center' justifyContent='space-between' sx={editMenuHalfMarginBelow}>
              <Typography sx={editMenuTitleSx}>{t('admin.customReports.editReports.editReportDetails')}</Typography>
              <IconButton
                aria-label={t('buttonText.close')}
                onClick={handleDismissEditMenu}
                data-testid='edit-menu-dismiss-button'
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <Typography sx={editMenuSubTextSx}>{t('formMessage.requiredExplanation')}</Typography>
            <Grid container gap={2}>
              <TextField
                label={t('admin.centralReportsPage.createReport.reportName')}
                value={editTitleValue}
                variant='standard'
                inputProps={{ maxLength: 50, 'data-testid': 'edit-report-name-input' }}
                fullWidth
                required
                error={editTitleValue?.length === 0 || isDuplicateTitle}
                helperText={
                  isDuplicateTitle ? t('admin.centralReportsPage.createReport.duplicateTitleError') : undefined
                }
                onChange={(e) => setEditTitleValue(e.target.value)}
              />
              <TextField
                label={t('admin.centralReportsPage.createReport.reportDescription')}
                value={editDescriptionValue}
                onChange={(e) => setEditDescriptionValue(e.target.value)}
                variant='standard'
                multiline
                inputProps={{ maxLength: 150, 'data-testid': 'edit-report-description-input' }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Popover>
      </Draggable>

      {/* Edit popup for filters */}
      <EditFiltersPopover
        anchorEl={editFilterAnchorEl}
        onClose={handleCloseEditFilters}
        onDismiss={handleDismissEditMenu}
        filters={filters}
        selectedFilters={selectedFilters}
        onToggleFilter={handleToggleFilter}
      />

      {/* --------------------- PAGE LAYOUT --------------------- */}

      {/* Report editing toolbar section */}
      {createFormData || isEdit ? (
        <Grid
          container
          sx={customReportHeaderBarSx}
          display='flex'
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          {isEdit ? t('admin.customReports.editReport') : t('admin.customReports.createReport')}
          <Grid sx={customReportHeaderButtonGroupSx}>
            <IconButton aria-label='undo' onClick={undo} data-testid='undo-button' disabled={!canUndo}>
              <SvgIcon component={UndoSvg} />
            </IconButton>
            <IconButton aria-label='redo' onClick={redo} data-testid='redo-button' disabled={!canRedo}>
              <SvgIcon component={RedoSvg} />
            </IconButton>
            <Button
              variant='outlined'
              sx={headerButtonsSx}
              onClick={() => {
                setUnsavedPromptOpen(true);
                setReturning(true);
              }}
            >
              {t('buttonText.cancel')}
            </Button>
            <Button variant='contained' sx={headerButtonsSx} onClick={isEdit ? handleSaveEditedMetadata : handleSave}>
              {t('buttonText.save')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Breadcrumbs
          breadcrumbLabels={[t(`navItems.reports`), title ?? '']}
          linkLocationOverride={`${routes.ALL_REPORTS}`}
          styleOverrides={breadcrumbStyles}
        />
      )}

      {/* Report Section */}
      <Grid sx={{ ...pagePadding, ...customReportRootSx }} container direction='column'>
        {/* Header Section */}
        <Grid
          container
          sx={isEditMode ? paddingX3 : {}}
          data-testid='header-section'
          tabIndex={0}
          onMouseEnter={() => handleSetActiveSection('description')}
          onFocus={() => handleSetActiveSection('description')}
        >
          <Grid item xs={isViewMode ? 6 : 12}>
            <Header
              dataTestId='custom-reports-header'
              title={title ?? ''}
              maxDescriptionWidth='600px'
              description={description}
              action={
                isEditMode &&
                activeSection === 'description' && (
                  <IconButton
                    aria-label={t('admin.customReports.editReport')}
                    onClick={handleOpenEditTitleMenu}
                    sx={{ marginTop: -5 }}
                    data-testid='custom-reports-edit-title-button'
                  >
                    <EditIcon />
                  </IconButton>
                )
              }
            />
          </Grid>

          {isViewMode && (
            <Grid item xs={6} sx={customReportViewHeaderGroupSx}>
              <Typography variant='body2' color='text.secondary'>
                {t('admin.customReports.lastEdit', {
                  date: formatDate(reportData?.updatedAt ?? Date.now(), preferences),
                  user: reportData?.updatedBy, // TODO: updatedBy is not being pulled correctly, as more implementation is needed in asm-reporting-service
                })}
              </Typography>
              {isScheduleReportEnabled && isReportActivelyScheduled && (
                <Box sx={lastEditedDateBoxSx}>
                  <DateRange sx={lastEditedDateIconSx} />
                </Box>
              )}
              <IconButton
                onClick={(e) => setEditMenuAnchorEl(e.currentTarget)}
                data-testid='custom-reports-menu-button'
                sx={customReportsMenuButtonSx}
              >
                <MoreVert />
              </IconButton>
            </Grid>
          )}
        </Grid>

        {/* Full screen loader while report details are being fetched */}
        {isLoadingReportDetails && (
          <Grid container alignItems='center' justifyContent='center' data-testid='spinner-container'>
            <CircularProgress />
          </Grid>
        )}

        {/* Filters Section, with a check for report details loading so default filters don't show alone before everything else */}
        {!isLoadingReportDetails && (
          <Grid
            container
            sx={isEditMode ? paddingX3 : {}}
            direction='row'
            justifyContent='space-between'
            data-testid='filters-section'
            onMouseEnter={() => handleSetActiveSection('filters')}
            onFocus={() => handleSetActiveSection('filters')}
          >
            <Grid item sx={filtersFlexGrowSx}>
              <ReportFilters
                key={reportData?.title}
                filters={filters.filter((f) => watchedFilters.includes(f.filterType as FilterReportType))}
                onFilterChange={setFilterValues}
                reportFilters={filterValues}
              />
            </Grid>
            <Grid item sx={{ alignSelf: 'center' }}>
              {isEditMode && activeSection === 'filters' && (
                <IconButton onClick={(e) => handleOpenEditFilters(e)} data-testid='edit-filters-button'>
                  <EditIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        )}
        {/* Loop through and render all grids */}
        {sectionList}
      </Grid>
    </FormProvider>
  );
}

export default CustomReports;
